import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from '@crabs-ai/ds-react';
import { isAxiosError } from 'axios';
import { VideoEditorTTSVoice } from '@/shared/apis/tts/get-tts-voices';
import { useTranslations } from 'next-intl';
import { useIsMutating } from '@tanstack/react-query';
import {
  usePostTtsGenerateApi,
  ttsGenerateMutationKey,
} from '@/shared/apis/tts/post-tts-generate';
import { ScriptSceneGroup } from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/script/ScriptContent';
import { TTSVoice } from '@/shared/components/dialog/TtsDialog/TtsDialogContext';
import {
  DEFAULT_EMOTION,
  DEFAULT_PITCH,
  DEFAULT_TEMPO,
} from '@/shared/constants/default-voice';
import { DtoEmotion, DtoVoice } from '@/shared/constants';
import {
  useCurrentStateAsRef,
  useWriteContext,
} from '@/features/remotion-editor/editor/utils/use-context';
import { regenerateCaptionAudioV2 } from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/script/utils/regenerate-caption-audiov2';
import { PlayerRef } from '@remotion/player';
import { getEditorDataApi } from '@/shared/apis/editor/get-editor-data';
import TtsTextCard from '@/features/remotion-editor/editor/editor-property-panel/ui/common/TtsTextCard';
import { generateIdempotencyKey } from '@/shared/utils/generate-idempotency-key';
import { isTtsBlockedError } from '@/shared/lib/tts-blocked-error';
import { shouldSyncScriptTextFromInitialText } from './script-text-sync';
import {SceneNarrationPreview} from '@waka/compat/SceneNarrationPreview';
import {hasSceneVoiceStage,stageSceneVoice,subscribeSceneVoiceApplied,editorUserMessage,matchesSubmittedSceneVoice,shouldRefreshSceneVoiceChoice,hasNewerSceneVoiceDraft} from '@waka/host/scene-voice';

interface ScriptContentSceneCardProps {
  cardId: string;
  projectId?: string;
  storyId?: string;
  sceneLabel: string;
  isActive: boolean;
  timelineLabel: string;
  initialText: string;
  voiceId?: string | null;
  audioSrc?: string | null;
  onSeekToScene?: () => void;
  voiceData?: VideoEditorTTSVoice[];
  scene: ScriptSceneGroup;
  playerRef: React.RefObject<PlayerRef | null>;
}

function ScriptContentSceneCard({
  cardId,
  projectId,
  storyId,
  sceneLabel,
  isActive,
  timelineLabel,
  initialText,
  voiceId,
  voiceData,
  audioSrc,
  onSeekToScene,
  scene,
  playerRef,
}: ScriptContentSceneCardProps) {
  const t = useTranslations();

  // @FIXME 2025-01-29: 기존 DB에 저장된 대문자 voice code (예: "Coral")와 신규 소문자 code 호환을 위한 임시 정규화
  // @TODO 2025-04-29: 3개월 후 기존 데이터 마이그레이션 완료 시 toLowerCase() 제거 가능
  const normalizeVoiceCode = useCallback(
    (value?: string | null) => (value ?? '').toLowerCase(),
    [],
  );

  // @FIXME 2025-01-29: emotion code 호환성 처리 (예: "normal" vs "normal-1", "normal-2")
  // voice별로 emotion code 형식이 다를 수 있어 -숫자 suffix를 제거하여 비교
  const normalizeEmotionCode = useCallback(
    (code?: string | null) => (code ?? '').replace(/-\d+$/, ''),
    [],
  );

  const stateAsRef = useCurrentStateAsRef();
  const { setState } = useWriteContext();

  const textContentRef = useRef<HTMLParagraphElement | null>(null);

  const [selectedTtsVoice, setSelectedTtsVoice] = useState<TTSVoice | null>(
    null,
  );
  const [originText, setOriginText] = useState(initialText);
  const [currentText, setCurrentText] = useState(initialText);
  const [originalTtsVoice, setOriginalTtsVoice] = useState<TTSVoice | null>(
    null,
  );
  const [isTextFocused, setIsTextFocused] = useState(false);
  const draftRevision=useRef(0),submittedRevision=useRef<number|null>(null);
  const currentTextRef=useRef(currentText);
  useLayoutEffect(()=>{currentTextRef.current=currentText;},[currentText]);
  const originalVoiceRef=useRef<TTSVoice|null>(null);
  useLayoutEffect(()=>{originalVoiceRef.current=originalTtsVoice;},[originalTtsVoice]);
  useEffect(()=>subscribeSceneVoiceApplied(value=>{
    const ids=scene.clips.map(clip=>clip.itemId).sort();
    if(!hasNewerSceneVoiceDraft(draftRevision.current,submittedRevision.current)&&value.source.selection.sceneKey===scene.sceneKey&&JSON.stringify([...value.source.captionIds].sort())===JSON.stringify(ids)&&matchesSubmittedSceneVoice(currentText,selectedTtsVoice,value)){
      // Clear only the submitted draft's marker; newer text or voice choices stay dirty.
      setOriginText(currentText);setOriginalTtsVoice(selectedTtsVoice);
    }
  }),[scene.sceneKey,scene.clips,currentText,selectedTtsVoice]);

  const lastPointerDownAtRef = useRef(0);
  const lastInitialTextRef = useRef(initialText);

  const syncTextElement = useCallback((text: string) => {
    const textElement = textContentRef.current;
    if (!textElement) return;

    if (textElement.textContent !== text) {
      textElement.textContent = text;
    }
  }, []);

  // projectId 는 props 로 string 으로 들어오므로 mutationKey/숫자 파라미터용으로 1회 변환해둔다.
  // useIsMutating 의 filter 는 mutationKey 배열이 동일해야 매치되므로 usePostTtsGenerateApi 와 반드시 같은 값을 사용.
  const numericProjectIdForKey = useMemo(() => {
    if (projectId == null) return undefined;
    const n = Number(projectId);
    return Number.isFinite(n) ? n : undefined;
  }, [projectId]);

  const { mutateAsync, isPending } = usePostTtsGenerateApi(
    numericProjectIdForKey,
  );

  // β 가드: 같은 project 에 대해 진행 중인 TTS 생성 mutation 수.
  // 다른 scene 카드에서 생성 중이면 나의 isPending 은 false 인데도 activeMutations > 0 이 된다.
  // → actionDisabled 로 "내 카드가 아닌 다른 곳이 작업 중"을 구분해 중복 발사를 차단.
  const activeProjectMutations = useIsMutating({
    mutationKey: ttsGenerateMutationKey(numericProjectIdForKey),
    exact: true,
  });
  const hasOtherScenePending = activeProjectMutations > 0 && !isPending;

  const tracks = stateAsRef.current?.undoableState?.tracks ?? [];
  const audioTrack = tracks.find(
    (track) => track.metadata?.type === 'audio' && track.metadata?.isMainTrack,
  );
  const captionTrack = tracks.find(
    (track) =>
      track.metadata?.type === 'caption' && track.metadata?.isMainTrack,
  );

  const isAudioLocked = audioTrack?.locked ?? false;
  const isCaptionLocked = captionTrack?.locked ?? false;
  const isAudioOrCaptionLocked = isAudioLocked || isCaptionLocked;

  useLayoutEffect(() => {
    const textElement = textContentRef.current;
    if (!textElement) return;

    if (
      !shouldSyncScriptTextFromInitialText({
        currentText,
        originText,
      })
    ) {
      return;
    }

    syncTextElement(currentText ?? '');
  }, [currentText, originText, syncTextElement]);

  useEffect(() => {
    if (lastInitialTextRef.current === initialText) {
      return;
    }

    if (hasNewerSceneVoiceDraft(draftRevision.current,submittedRevision.current)) {
      lastInitialTextRef.current=initialText;
      setOriginText(initialText);
      return;
    }
    if (
      !shouldSyncScriptTextFromInitialText({
        currentText,
        originText,
      })
    ) {
      return;
    }

    lastInitialTextRef.current = initialText;
    setOriginText(initialText);
    setCurrentText(initialText);
    syncTextElement(initialText);
  }, [currentText, initialText, originText, syncTextElement]);

  const findVoice = useMemo(() => {
    if (voiceData) {
      const normalizedVoiceId = normalizeVoiceCode(voiceId);
      const found = voiceData.find(
        (voice) => normalizeVoiceCode(voice.code) === normalizedVoiceId,
      );
      if (found) return found;
      // 저장된 voice가 catalog에 없으면 BE catalog의 현재 첫 후보만 사용한다.
      return voiceData[0];
    }
  }, [normalizeVoiceCode, voiceId, voiceData]);

  useEffect(() => {
    if (voiceId && voiceData) {
      const normalizedVoiceId = normalizeVoiceCode(voiceId);
      const voice = voiceData.find(
        (v) => normalizeVoiceCode(v.code) === normalizedVoiceId,
      );
      const emotion = scene.emotion
        ? findVoice?.emotions.find(
            (item) =>
              normalizeEmotionCode(item.code) ===
              normalizeEmotionCode(scene.emotion),
          )
        : findVoice?.emotions.find(
            (item) =>
              item.code === (voice?.emotions[0]?.code || DEFAULT_EMOTION),
          );

      if (voice && emotion) {
        const ttsVoice: TTSVoice = {
          voice: voice,
          tempo: scene.speedFactor || 1,
          pitch: scene.pitch || 0,
          emotion: emotion,
          style: scene.style,
        };
        const baseline=originalVoiceRef.current;
        setSelectedTtsVoice(current=>hasSceneVoiceStage()&&(hasNewerSceneVoiceDraft(draftRevision.current,submittedRevision.current)||!shouldRefreshSceneVoiceChoice(current,baseline))?current:ttsVoice);
        setOriginalTtsVoice(ttsVoice);
      }
    }
  }, [
    voiceId,
    voiceData,
    scene,
    findVoice,
    normalizeEmotionCode,
    normalizeVoiceCode,
  ]);

  const isTextDirty = currentText !== originText;
  const isVoiceDirty = useMemo(() => {
    if (!selectedTtsVoice && !originalTtsVoice) return false;
    if (!selectedTtsVoice || !originalTtsVoice) return true;

    return (
      selectedTtsVoice.voice.code !== originalTtsVoice.voice.code ||
      selectedTtsVoice.tempo !== originalTtsVoice.tempo ||
      selectedTtsVoice.pitch !== originalTtsVoice.pitch ||
      selectedTtsVoice.emotion?.code !== originalTtsVoice.emotion?.code ||
      (selectedTtsVoice.style?.trim()??'') !== (originalTtsVoice.style?.trim()??'')
    );
  }, [originalTtsVoice, selectedTtsVoice]);

  const isDirty = isTextDirty || isVoiceDirty;

  const cardBorderClass = useMemo(() => {
    if (isTextFocused && isDirty)
      return 'border-l-[4px] border-comp-label-yellow-bg';
    if (isTextFocused) return 'border-l-[4px] border-sys-style-primary-default';
    if (isDirty) return 'border-l-[4px] border-comp-label-red-bg';
    return '';
  }, [isDirty, isTextFocused]);

  const handleTextPointerDown = useCallback(() => {
    lastPointerDownAtRef.current = Date.now();
    onSeekToScene?.();
  }, [onSeekToScene]);

  const handleTextFocus = useCallback(() => {
    setIsTextFocused(true);
    const lastPointerDownAt = lastPointerDownAtRef.current;
    if (Date.now() - lastPointerDownAt <= 200) return;
    onSeekToScene?.();
  }, [onSeekToScene]);

  const handleTextBlur = useCallback((text: string) => {
    setIsTextFocused(false);
    if(text!==currentTextRef.current){draftRevision.current++;currentTextRef.current=text;}
    setCurrentText(text);
  }, []);

  const handleTextInput = useCallback((text: string) => {
    if(text!==currentTextRef.current){draftRevision.current++;currentTextRef.current=text;}
    setCurrentText(text);
  }, []);

  const handleResetVoiceSettings = useCallback(() => {
    if (!originalTtsVoice) return;
    draftRevision.current++;
    setSelectedTtsVoice(originalTtsVoice);
  }, [originalTtsVoice]);

  const handleRegenerate = useCallback(async () => {
    if (!hasSceneVoiceStage()) {
      toast({type:'error',description:'와카 작업에 연결한 정밀 편집에서 장면 음성을 다시 만들어 주세요.',toastOptions:{position:'top-center'}});
      return;
    }
    // Native Waka scenes have authoring keys, not company API scene numbers.
    if (hasSceneVoiceStage() || scene.sceneKey.startsWith('waka:')) {
      try {
        if (!selectedTtsVoice?.voice?.code || !selectedTtsVoice?.emotion?.code) throw new Error('목소리와 감정을 먼저 선택해 주세요.');
        submittedRevision.current=draftRevision.current;
        await stageSceneVoice({selection:{sceneKey:scene.sceneKey,captionIds:scene.clips.map(clip=>clip.itemId)},text:currentText,
          settings:{voiceId:selectedTtsVoice.voice.code,speed:selectedTtsVoice.tempo??DEFAULT_TEMPO,emotion:selectedTtsVoice.emotion.code,pitch:selectedTtsVoice.pitch??0,...(selectedTtsVoice.style?.trim()?{style:selectedTtsVoice.style.trim()}:{})}});
        toast({type:'success',description:'편집본을 저장했습니다. 와카 상단에서 음성 생성과 자막 인식을 확인해 주세요.',toastOptions:{position:'top-center'}});
      } catch (error) {
        toast({type:'error',description:editorUserMessage(error),toastOptions:{position:'top-center'}});
      }
      return;
    }
    try {
      if (!scene.sceneId || !projectId) {
        console.error('handleRegenerate stop: missing sceneId or projectId');
        return;
      }
      const numericProjectId = Number(projectId);
      const numericStoryId = Number(storyId);
      if (Number.isNaN(numericStoryId)) {
        console.error('handleRegenerate stop: invalid storyId', storyId);
        return;
      }
      if (Number.isNaN(numericProjectId)) {
        console.error('handleRegenerate stop: invalid projectId', projectId);
        return;
      }

      if (isAudioOrCaptionLocked) {
        toast({
          type: 'error',
          description: t('Editor.toast.unlock-audio-caption-required'),
          toastOptions: { position: 'top-center' },
        });
        return;
      }

      if (selectedTtsVoice?.voice?.code && selectedTtsVoice?.emotion?.code) {
        const res = await mutateAsync({
          projectId: numericProjectId,
          sceneId: scene.sceneId ?? undefined,
          idempotencyKey: generateIdempotencyKey(),
          text: currentText,
          voice: selectedTtsVoice.voice.code as DtoVoice,
          speedFactor: selectedTtsVoice.tempo ?? DEFAULT_TEMPO,
          pitch: selectedTtsVoice.pitch ?? DEFAULT_PITCH,
          emotion: selectedTtsVoice.emotion.code as DtoEmotion,
          style: selectedTtsVoice.style, // Gemini 전용, 다른 벤더는 무시
        });

        const editorData = await getEditorDataApi(
          numericProjectId,
          numericStoryId,
        );
        if (!editorData?.data) {
          console.error(
            'handleRegenerate stop: invalid editor data',
            editorData,
          );
          return;
        }

        await regenerateCaptionAudioV2({
          tts: res.data.data,
          editorData: editorData.data,
          editorState: stateAsRef.current,
          sceneId: scene.sceneId,
          setState,
          projectId: numericProjectId,
          sceneVideoEnd: scene.videoEnd,
        });

        // 재생성 성공 후 상태 업데이트 (isDirty = false)
        setOriginText(currentText);
        setOriginalTtsVoice(selectedTtsVoice);
      }
    } catch (error) {
      console.error('handleRegenerate error', error);
      // 422 정책 차단 (1031/1032) 은 전역 TtsBlockedDialog 가 흡수 — 추가 toast skip.
      if (isTtsBlockedError(error)) return;
      // 상태별 사용자 피드백:
      //  - 409 Conflict: 멱등성 락 중복 — "이미 진행 중" 안내
      //  - 402 (포인트 부족, code=1301): coin-provider 전역 interceptor 가 CoinShortageDialog 표시 — 여기서 중복 처리 금지
      //  - 400 (validation): FE/BE 계약 위반 (개발자 이슈) — 콘솔 로그만
      //  - 기타 (500/타임아웃/네트워크): 일반 실패 toast
      if (isAxiosError(error)) {
        const status = error.response?.status;
        if (status === 409) {
          toast({
            type: 'error',
            description: t('Editor.toast.tts-regenerate-in-progress'),
            toastOptions: { position: 'top-center' },
          });
        } else if (status !== 402 && status !== 400) {
          toast({
            type: 'error',
            description: t('Editor.toast.tts-regenerate-failed'),
            toastOptions: { position: 'top-center' },
          });
        }
      } else {
        // axios 가 아닌 예외 (로컬 코드 버그) 도 최소한의 사용자 피드백.
        toast({
          type: 'error',
          description: t('Editor.toast.tts-regenerate-failed'),
          toastOptions: { position: 'top-center' },
        });
      }
    }
  }, [
    currentText,
    isAudioOrCaptionLocked,
    mutateAsync,
    projectId,
    scene.sceneId,
    scene.sceneKey,
    scene.clips,
    scene.videoEnd,
    selectedTtsVoice,
    setState,
    stateAsRef,
    storyId,
    t,
  ]);

  const previewFps = stateAsRef.current.undoableState.fps;
  return (
    <TtsTextCard
      cardId={cardId}
      label={sceneLabel}
      timelineLabel={timelineLabel}
      isActive={isActive}
      isTextDisabled={isPending}
      isLocked={isAudioOrCaptionLocked}
      audioSrc={audioSrc}
      audioPreview={scene.sceneKey.startsWith('waka:') ? <SceneNarrationPreview start={Math.round(scene.videoStart*previewFps/1000)} end={Math.round(scene.videoEnd*previewFps/1000)} playerRef={playerRef} /> : undefined}
      selectedVoice={selectedTtsVoice}
      onVoiceChange={voice=>{draftRevision.current++;setSelectedTtsVoice(voice);}}
      textContentRef={textContentRef}
      onTextPointerDown={handleTextPointerDown}
      onTextFocus={handleTextFocus}
      onTextBlur={handleTextBlur}
      onTextInput={handleTextInput}
      cardBorderClass={cardBorderClass}
      isDirty={isDirty}
      isVoiceDirty={isVoiceDirty}
      isPending={isPending}
      onAction={handleRegenerate}
      onResetVoiceSettings={handleResetVoiceSettings}
      actionLabel={t('Editor.script-panel.regenerate')}
      actionDisabled={hasOtherScenePending}
      playerRef={playerRef}
    />
  );
}

export default ScriptContentSceneCard;
